import { contentSeriesTagColors, Course } from '@/client/courses';
import {
  CourseSchedulePeriodUnit,
  PlannerCoursesFiltersType,
  PlannerCRUDCourse,
} from '@/client/planner/types';
import { DEFAULT_ENROLLMENT_HOUR } from '@/common/constants';
import { useAppSelector } from '@/hooks/store';
import PagePlaceholderImage from '@/images/placeholders/page-template.svg';
import { selectCurrentAccount } from '@/store/features/account';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { formatTags } from '@/utils/helpers';
import { applyPlannerCoursesFilters } from '@/utils/planner/planner.helper';
import { t } from 'i18next';
import moment from 'moment';
import { Dropdown } from 'primereact/dropdown';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { PlannerCoursesFilters } from './PlannerCoursesFilters';

const StyledAppButton = styled(AppButton)`
  &.p-button.p-button-icon-only {
    margin: auto 16px;
    border: 0 !important;
    box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.15);
  }

  &.p-button.p-button-secondary:enabled:hover {
    border: 0 !important;
  }
`;

const StyledDropdown = styled(Dropdown)`
  &.p-dropdown {
    visibility: hidden;
    width: 0px;
    height: 0px;
    top: -150px;
    left: 25px;
    position: relative;
  }
`;

const StyledSpan = styled.span`
  margin-top: var(--small-padding);
  color: var(--black-main);
`;

const StyledDateText = styled.div`
  color: var(--gray-darker);
  margin-left: auto;
`;

const CourseItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
  width: 100%;
  min-height: 60px;

  .course-thumbnail {
    border-radius: var(--xxxsmall-border-radius);
    width: 56px;
    height: 39px;
    background-size: contain;
    background-position: center center;
    background-repeat: no-repeat;
    background-color: var(--gray-pale);
  }
`;

const CourseInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  align-self: center;
  align-items: flex-start;
  gap: 3px;
`;

const CourseTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xxsmall-padding);
  flex-wrap: wrap;
`;

const CourseTitle = styled.div`
  font-weight: 500;
  color: var(--black-main);
  font-size: var(--small-font-size);
`;

const CourseTags = styled.div`
  font-size: var(--xxsmall-font-size);
  line-height: 150%;
  color: var(--gray-darker);
`;

const StyledChip = styled(AppChip)`
  font-weight: 500;
  background: var(--platinum-main);
  width: auto;

  .gray {
    color: var(--gray-darkest);
    border-radius: 100px;
  }
`;

type PlannerCourseAddButtonProps = {
  courses?: Course[];
  timelineItems?: PlannerCRUDCourse[];
  currentTimelineItem?: PlannerCRUDCourse;
  onItemAdd: (item: PlannerCRUDCourse) => void;
  isOnboarding?: boolean;
  isCoursesLoading?: boolean;
};

export const PlannerCourseAddButton: React.FC<PlannerCourseAddButtonProps> = ({
  courses,
  timelineItems,
  currentTimelineItem,
  onItemAdd,
  isOnboarding,
  isCoursesLoading,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const dropdownRef = useRef<Dropdown>(null);

  const [filters, setFilters] = useState<PlannerCoursesFiltersType>({});

  const options = useMemo(() => {
    const customCoursesList =
      courses?.filter(
        (course) =>
          !!course.account?.id &&
          (!course.plannerScheduleDate ||
            moment(course.plannerScheduleDate).isSameOrBefore(
              moment(),
              'days',
            )),
      ) ?? [];

    const platformCoursesList =
      courses?.filter(
        (course) =>
          !course.account?.id &&
          (!course.plannerScheduleDate ||
            moment(course.plannerScheduleDate).isSameOrBefore(
              moment(),
              'days',
            )),
      ) ?? [];

    const upcomingCoursesList = !isOnboarding
      ? courses?.filter(
          (course) =>
            !!course.plannerScheduleDate &&
            moment(course.plannerScheduleDate).isAfter(moment(), 'days'),
        ) ?? []
      : [];

    const customCourses = applyPlannerCoursesFilters(
      customCoursesList,
      filters,
    );
    const platformCourses = applyPlannerCoursesFilters(
      platformCoursesList,
      filters,
    );
    const upcomingCourses = applyPlannerCoursesFilters(
      upcomingCoursesList,
      filters,
    );

    return [
      ...(customCourses?.length
        ? [
            {
              label: t('content.for.accountName', {
                accountName: currentAccount?.name,
              }),
              items: customCourses,
            },
          ]
        : []),
      ...(platformCourses?.length
        ? [
            {
              label: t('content.for.accountName', {
                accountName: currentAccount?.platformSettings?.name,
              }),
              items: platformCourses,
            },
          ]
        : []),
      ...(upcomingCourses?.length
        ? [
            {
              label: t('content.upcoming.platformName', {
                platformName: currentAccount?.platformSettings?.name,
              }),
              items: upcomingCourses,
            },
          ]
        : []),
    ];
  }, [courses, filters, currentAccount, isOnboarding, t]);

  const toggleDropdown = () => {
    if (!dropdownVisible) {
      dropdownRef?.current?.show();
    } else {
      dropdownRef?.current?.hide();
    }
    setDropdownVisible(!dropdownVisible);
  };

  const optionItemTemplate = (course: Course) => {
    const isUpcomingCourse =
      course.plannerScheduleDate &&
      moment(course.plannerScheduleDate).isAfter(moment(), 'days');
    const isCustom = !!course.account?.id;

    return (
      <CourseItemContainer>
        <div
          className="course-thumbnail"
          style={{
            backgroundImage: `url(${CSS.escape(
              course?.courseThumbnail?.signedUrl ?? PagePlaceholderImage,
            )})`,
          }}
        />
        <CourseInfo>
          <CourseTitleRow>
            <CourseTitle>{course.name}</CourseTitle>
            {course.contentSeries && (
              <AppChip
                label={t(`content.series.${course.contentSeries}`)}
                type={contentSeriesTagColors[course.contentSeries]}
                size="xs-auto"
              />
            )}
            {isCustom && (
              <AppChip label={t('generic.custom')} type="red" size="xs-auto" />
            )}
            {isUpcomingCourse && (
              <StyledDateText>
                {moment(course.plannerScheduleDate).format(
                  DateFormats.DATEONLY_SHORT,
                )}
              </StyledDateText>
            )}
          </CourseTitleRow>

          {!!course.tags?.length && (
            <CourseTags>
              {t('content.tags')}: {formatTags(course.tags || [])}
            </CourseTags>
          )}
          <StyledChip
            label=""
            template={enrolledTemplate(course)}
            type="beige"
            size="xs"
          />
        </CourseInfo>
      </CourseItemContainer>
    );
  };

  const enrolledTemplate = (course: Course) => (
    <>
      <span>{course.totalNumberOfEnrollments || 0}</span>
      <span className="gray">
        {t('courses.alreadyEnrolled', { count: course.activeUsersCount || 0 })}
      </span>
    </>
  );

  const onFilterChange = useCallback(
    (newFilters: PlannerCoursesFiltersType) => {
      setFilters(newFilters);
    },
    [],
  );

  const filterTemplate = () => {
    return (
      <PlannerCoursesFilters
        filters={filters}
        onFilterChange={onFilterChange}
      />
    );
  };

  return (
    <>
      <StyledAppButton
        icon={isCoursesLoading ? 'pi pi-spinner pi-spin' : ' pi pi-plus'}
        severity="secondary"
        type="outlined"
        isDisabled={isCoursesLoading}
        onClick={() => toggleDropdown()}
      />
      {!timelineItems?.length && (
        <StyledSpan>{t('planner.addContent')}</StyledSpan>
      )}
      {courses && (
        <StyledDropdown
          panelClassName="planner-course-dropdown"
          ref={dropdownRef}
          filter
          optionLabel="name"
          optionGroupLabel="label"
          optionGroupChildren="items"
          options={options}
          scrollHeight="350px"
          itemTemplate={optionItemTemplate}
          filterTemplate={filterTemplate}
          onChange={(e) => {
            const sharedProps = {
              ...e.value,
              periodValue: currentTimelineItem?.periodValue || 0,
              periodUnit:
                currentTimelineItem?.periodUnit ||
                CourseSchedulePeriodUnit.DAYS,
            };

            // Upcoming platform course: always lands on its own scheduled date
            const isUpcoming =
              e.value.plannerScheduleDate &&
              moment(e.value.plannerScheduleDate).isAfter(moment(), 'day');

            if (isUpcoming) {
              onItemAdd({
                ...sharedProps,
                enrollDate: moment
                  .utc(e.value.plannerScheduleDate)
                  .set({ h: 9, m: 0 })
                  .toDate(),
                enrollmentHour: DEFAULT_ENROLLMENT_HOUR,
                enrollImmediately: false,
              });
              return;
            }

            // Regular course: inherit the date group the + button belongs to
            const groupDate = currentTimelineItem?.enrollDate
              ? moment(currentTimelineItem.enrollDate).startOf('day')
              : moment().startOf('day');
            const isGroupToday = groupDate.isSame(moment(), 'day');
            const nextHour = new Date().getHours() + 1;
            const enrollImmediately = isGroupToday && nextHour >= 24;

            onItemAdd({
              ...sharedProps,
              enrollDate: isGroupToday
                ? new Date()
                : moment
                    .utc([
                      groupDate.year(),
                      groupDate.month(),
                      groupDate.date(),
                      9,
                      0,
                    ])
                    .toDate(),
              enrollmentHour: isGroupToday
                ? nextHour < 24
                  ? nextHour
                  : 10
                : currentTimelineItem?.enrollmentHour ??
                  DEFAULT_ENROLLMENT_HOUR,
              enrollImmediately,
            });
          }}
          optionDisabled={(option) =>
            !!isOnboarding &&
            !!timelineItems?.some((item: any) => item?.id === option?.id)
          }
          onHide={() => {
            setDropdownVisible(false);
          }}
        />
      )}
    </>
  );
};
